import { RefObject, useEffect, useLayoutEffect, useRef } from "react";

/**
 * The current step's question heading: the first `h2` in the container that is
 * not part of a `role="status"` message. A status heading is skipped so a polite
 * announcement (the submission success screen) is not interrupted by a focus move.
 */
export const findStepHeading = (container: HTMLElement): HTMLElement | null =>
  Array.from(container.querySelectorAll<HTMLElement>("h2")).find(
    (heading) => !heading.closest('[role="status"]'),
  ) ?? null;

/**
 * When the element that had focus inside `containerRef` is removed from the DOM
 * (a step remounts, a sub-screen swaps), the browser drops focus to `<body>` and a
 * screen reader falls out of the dialog. This moves focus to `findTarget(container)`
 * instead.
 *
 * It acts only when focus was lost to removal: a real blur (clicking blank space,
 * moving focus outside the container) clears the record, and an element that took
 * focus during the same commit (an `autoFocus` input) wins because focus is then no
 * longer on `<body>`.
 *
 * Observes `childList` only. Setting `tabindex` on the target must not retrigger it.
 */
export const useRefocusWhenFocusedNodeRemoved = (
  containerRef: RefObject<HTMLElement | null>,
  findTarget: (container: HTMLElement) => HTMLElement | null,
) => {
  const findTargetRef = useRef(findTarget);
  useLayoutEffect(() => {
    findTargetRef.current = findTarget;
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let lastFocused: Element | null = null;

    const onFocusIn = (e: FocusEvent) => {
      lastFocused = e.target as Element;
    };

    const onFocusOut = (e: FocusEvent) => {
      const left = e.target as Element;
      const next = e.relatedTarget as Node | null;
      if (next && !container.contains(next)) {
        lastFocused = null;
        return;
      }
      // Chromium fires focusout when a focused node is removed, so only a node that
      // is still in the document after this event counts as a real blur.
      queueMicrotask(() => {
        if (lastFocused === left && left.isConnected && document.activeElement !== left) {
          lastFocused = null;
        }
      });
    };

    // The record is kept until focus is resolved, so a step whose content arrives in
    // a later commit (a loading frame first) is still recovered.
    const observer = new MutationObserver(() => {
      if (!lastFocused || lastFocused.isConnected) return;
      const active = document.activeElement;
      if (active && active !== document.body) {
        lastFocused = null;
        return;
      }
      const target = findTargetRef.current(container);
      if (!target) return;
      lastFocused = null;
      if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
      target.focus();
    });

    container.addEventListener("focusin", onFocusIn);
    container.addEventListener("focusout", onFocusOut);
    observer.observe(container, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      container.removeEventListener("focusin", onFocusIn);
      container.removeEventListener("focusout", onFocusOut);
      lastFocused = null;
    };
  }, [containerRef]);
};
